import {
  ArrowUturnLeftIcon,
  InformationCircleIcon
} from '@heroicons/react/24/solid'
import { usePathname, useSearchParams } from 'next/navigation'
import { useRouter } from 'next/router'
import { FC, useMemo } from 'react'

import { Premise } from '@/api/models/auth/lib/auth.types'
import { RequestByIdApiType } from '@/api/models/requests'
import { YandexMetrikaEvent } from '@/app/constants/metrics'
import CarIcon from '@/assets/svg/CarIcon.svg'
import { useYandexMetrika } from '@/components/shared/yandex-metrika/hooks/useYandexMetrika'

import { getAutosWithParkingNames } from './lib/utils'

type Props = {
  autos: RequestByIdApiType['autos']
  premises?: Premise[]
  anyParkingPlace?: boolean
  canAutoBeChanged?: boolean
}

const ContentBlockAutos: FC<Props> = ({
  premises = [],
  autos,
  anyParkingPlace = false,
  canAutoBeChanged = false
}) => {
  const router = useRouter()
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const { reachGoal } = useYandexMetrika()

  const autosWithParkingNames = useMemo(
    () => getAutosWithParkingNames(autos, premises),
    [autos, premises]
  )

  const changePageToEditing = (autoId: string) => {
    reachGoal(YandexMetrikaEvent.PASSES_EDIT_AUTO_START)

    const params = new URLSearchParams(searchParams?.toString() || '')
    params.set('editing', 'true')
    params.set('autoId', autoId)

    router.push(`${pathname}?${params}`)
  }

  return (
    <>
      {autosWithParkingNames && autosWithParkingNames.length > 0 && (
        <div className="flex flex-col gap-4">
          <p className="mb-4 font-bold text-grey-90 text-16">
            Транспортные средства
          </p>
          {autosWithParkingNames.map((car, carIndex) => (
            <div key={car.id || carIndex} className="flex flex-col gap-4">
              <div className="flex gap-8">
                <CarIcon className="relative top-3 h-20 w-20" />
                <div className="flex flex-col font-pro text-16">
                  <span>
                    {car.number}
                    {car.model ? `, ${car.model}` : ''}
                  </span>
                  {car.parkingPlace && (
                    <span className="inline-block truncate text-bronze-70">
                      Машино-место: {car.parkingPlace}
                    </span>
                  )}
                </div>
              </div>
              {canAutoBeChanged && car.id && (
                <button
                  className="mb-8 flex gap-8 text-left text-bronze-40 underline text-16 xxs:text-14"
                  type="button"
                  onClick={() => changePageToEditing(car.id)}
                >
                  <ArrowUturnLeftIcon className="h-20 w-20 shrink-0" />
                  Изменить данные о ТС
                </button>
              )}
            </div>
          ))}
          {anyParkingPlace && (
            <div className="mt-12 flex justify-between gap-8 rounded-16 bg-white p-12">
              <div className="h-24 w-24">
                <InformationCircleIcon className="h-24 w-24 text-bronze-40" />
              </div>
              <p className="text-16">
                Транспортное средство можно припарковать на любом свободном
                месте в зоне платного паркинга
              </p>
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default ContentBlockAutos
